import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { Button as MIButton } from '@material-ui/core';
import { ErrorInfo, isErrorInfo } from '../../../shared/interfaces/error-info.interface';
import { BlogUiModel } from '../../../shared/ui-models/blog.ui-model';
import { APP_ROUTES, PUBLIC_PATH } from '../../../shared/config/routes-config';
import { RouteConfig } from '../../../shared/interfaces/routes-config.interface';
import Button, { ButtonType } from '../../../shared/components/core-ui/button/button.component';
import { Pagination } from '../../../shared/components/core-ui/pagination/pagination.component';
import { birdToast } from '../../../shared/components/core-ui/toast';
import { BLOGS_PAGE_SIZE } from '../../shared/config/blog-module.config';
import {
  deleteBlog,
  getBlogs,
  publishBlog,
  unpublishBlog
} from '../../shared/services/data.service';
import { DeleteBlogModal } from './components/delete-blog-modal/delete-blog-modal.component';

export const BlogsListContainer = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [blogsList, setBlogsList] = useState<BlogUiModel[]>([]);
  const [blogsCount, setBlogsCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(BLOGS_PAGE_SIZE);
  const [blogToDelete, setBlogToDelete] = useState<BlogUiModel>();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const loadBlogs = useCallback(() => {
    getBlogs((page - 1) * itemsPerPage, itemsPerPage).then(
      (response: { list: BlogUiModel[]; count: number } | ErrorInfo) => {
        if (isErrorInfo(response)) {
          birdToast.error(t('BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.LOAD_FAILED.MESSAGE'));
          return;
        }
        setBlogsList(response.list);
        setBlogsCount(response.count);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, itemsPerPage]);

  useEffect(() => {
    loadBlogs();
  }, [loadBlogs]);

  const replaceInList = (updated: BlogUiModel) => {
    setBlogsList((current) =>
      current.map((blog: BlogUiModel) => (blog.id === updated.id ? updated : blog))
    );
  };

  const handlePublishToggle = (blog: BlogUiModel) => {
    if (!blog.id) return;

    const action = blog.published ? unpublishBlog : publishBlog;
    const promise = action(blog.id).then((response: BlogUiModel | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      replaceInList(response);
    });

    birdToast.promise(promise, {
      loadingMessage: blog.published ? 'Unpublishing blog...' : 'Publishing blog...',
      successMessage: blog.published
        ? t('BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.UNPUBLISH_SUCCESS.MESSAGE')
        : t('BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.PUBLISH_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.PUBLISH_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.PUBLISH_FAILED.MESSAGE')
    });
  };

  const handleBlogDelete = (blogId: string) => {
    const promise = deleteBlog(blogId).then((response: {} | ErrorInfo) => {
      if (isErrorInfo(response)) {
        throw response;
      }
      setBlogsList((current) => current.filter((blog: BlogUiModel) => blog.id !== blogId));
      setBlogsCount((current) => Math.max(0, current - 1));
    });

    birdToast.promise(promise, {
      loadingMessage: 'Deleting blog...',
      successMessage: t('BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.DELETE_SUCCESS.MESSAGE'),
      errorPermissionMessage: t(
        'BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.DELETE_PERMISSION.MESSAGE'
      ),
      errorFailedMessage: t('BLOG_MODULE.LIST_CONTAINER.NOTIFICATIONS.DELETE_FAILED.MESSAGE')
    });
  };

  const goToEdit = (blogId: string) => {
    history.push(
      (APP_ROUTES.BLOG_MODULE.CHILDREN as RouteConfig).EDIT.FULL_PATH.replace(':id', blogId)
    );
  };

  return (
    <div className="flex h-full flex-col items-start justify-start md:min-h-[calc(100vh-45px)] md:bg-[#f5f7fc] md:px-[2rem] md:py-[3rem]">
      <div className="flex w-full items-center justify-between gap-[1rem]">
        <div className="my-[1rem] text-[2.5rem] font-bold text-primary-500">
          {t('BLOG_MODULE.LIST_CONTAINER.TITLE')}
        </div>
        <Button
          type={ButtonType.primary}
          onClick={() => {
            history.push((APP_ROUTES.BLOG_MODULE.CHILDREN as RouteConfig).NEW.FULL_PATH);
          }}
        >
          {t('BLOG_MODULE.LIST_CONTAINER.ADD_BLOG')}
        </Button>
      </div>

      <div className="grid w-full items-stretch gap-[1.2rem] pb-[2rem] [grid-template-columns:1fr] md:[grid-template-columns:repeat(auto-fill,minmax(320px,1fr))] md:gap-[1.6rem]">
        {blogsList.map((blog, index) => (
          <div
            key={blog.id ? blog.id : `blog-${index}`}
            className="flex h-full w-full flex-col rounded-[4px] bg-white px-[1.4rem] py-[1.5rem] shadow-[0_2px_4px_0_rgba(187,186,186,0.5)]"
          >
            <div className="mb-[1rem] flex h-[150px] w-full flex-none items-center justify-center">
              <img
                className="h-full w-full rounded-[8px] object-cover"
                alt={blog.header || t('BLOG_MODULE.LIST_CONTAINER.UNTITLED_BLOG')}
                src={blog.image || `${PUBLIC_PATH}/assets/pngs/amenity-default.png`}
              />
            </div>

            <div className="mb-[0.6rem] text-left text-[1.8rem] font-bold text-[#37454d]">
              {blog.header || t('BLOG_MODULE.LIST_CONTAINER.UNTITLED_BLOG')}
            </div>
            <div className="mb-[0.6rem] text-right text-[1.6rem] font-bold text-[#37454d]">
              {blog.headerAr}
            </div>

            <div className="mb-[1rem] flex items-center gap-[0.6rem]">
              <span
                className={`rounded-[4px] px-[0.8rem] py-[0.2rem] text-[1.2rem] font-bold ${
                  blog.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {blog.published
                  ? t('BLOG_MODULE.LIST_CONTAINER.PUBLISHED')
                  : t('BLOG_MODULE.LIST_CONTAINER.DRAFT')}
              </span>
              {blog.publishedAt && (
                <span className="text-[1.2rem] text-gray-500">{blog.publishedAt}</span>
              )}
            </div>

            <div className="mt-auto flex w-full items-center justify-between gap-[0.4rem]">
              <Button
                type={blog.published ? ButtonType.gray : ButtonType.success}
                onClick={() => handlePublishToggle(blog)}
              >
                {blog.published
                  ? t('BLOG_MODULE.LIST_CONTAINER.UNPUBLISH')
                  : t('BLOG_MODULE.LIST_CONTAINER.PUBLISH')}
              </Button>
              <div className="flex items-center gap-[0.2rem] [&_button]:min-w-0">
                <MIButton
                  aria-label={t('BLOG_MODULE.LIST_CONTAINER.EDIT_LINK')}
                  onClick={() => {
                    if (blog.id) {
                      goToEdit(blog.id);
                    }
                  }}
                >
                  <EditIcon fontSize="large" color="inherit" />
                </MIButton>
                <MIButton
                  aria-label={t('BLOG_MODULE.LIST_CONTAINER.DELETE_LINK')}
                  onClick={() => {
                    setBlogToDelete(blog);
                    setShowDeleteModal(true);
                  }}
                >
                  <DeleteIcon fontSize="large" color="error" />
                </MIButton>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Pagination
        itemsPerPage={itemsPerPage}
        itemCounts={blogsCount}
        offset={page}
        handlePageChange={(pageNumber: number) => setPage(pageNumber)}
        handleItemsPerPage={(itemsNumber: number) => {
          setItemsPerPage(itemsNumber);
          setPage(1);
        }}
      />

      <DeleteBlogModal
        config={{ showModal: showDeleteModal }}
        data={{ blog: blogToDelete }}
        eventHandlers={{
          cancelHandler: () => {
            setShowDeleteModal(false);
            setBlogToDelete(undefined);
          },
          deleteBlogHandler: (blog: BlogUiModel) => {
            setShowDeleteModal(false);
            setBlogToDelete(undefined);
            if (blog && blog.id) {
              handleBlogDelete(blog.id);
            }
          }
        }}
      />
    </div>
  );
};

export default BlogsListContainer;
